import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronUp, ExternalLink } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatDate, formatNumber, formatRM } from "@/lib/format";
import { Attachment, Badge, Empty, ErrorNote, Loading } from "@/components/portal/Ui";
import { PlatformBadge, ReviewBadge } from "@/components/portal/PlatformBits";
import { campaignHref } from "@/pages/internal/TrackerOverview";

/** Read-only quick look that grows out of an Overview card. Work happens on the campaign page. */
export function CardDetail({ row, onCollapse }) {
  const [posts, setPosts] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [p, i] = await Promise.all([
        api.get("/posts", {
          params: {
            campaign_id: row.id,
          },
        }),
        api.get("/invoices", {
          params: { campaign_id: row.id },
        }),
      ]);
      setPosts(p.data);
      setInvoices(i.data);
      setError("");
    } catch (e) {
      setError(apiError(e));
      setPosts([]);
      setInvoices([]);
    }
  }, [row.id]);

  useEffect(() => {
    load();
  }, [load]);

  const showPosts = true;
  const showInvoices = true;

  return (
    <div
      className="mt-1 border-t border-[#EFEAE1] pt-4"
      onClick={(event) => event.stopPropagation()}
      data-testid={`tracker-card-detail-${row.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <span className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
          Everything on this campaign
        </span>
        <button
          type="button"
          onClick={onCollapse}
          data-testid={`detail-collapse-${row.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#6b6660] hover:underline"
        >
          <ChevronUp className="h-3.5 w-3.5" />
          Collapse
        </button>
      </div>

      {error && <ErrorNote message={error} />}

      {posts === null ? (
        <div className="py-6">
          <Loading label="Loading this campaign…" />
        </div>
      ) : (
        <div className="mt-4 space-y-5">
          {showPosts && (
            <section data-testid={`detail-posts-${row.id}`}>
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">
                  Content submissions ({posts.length})
                </h4>
                <Link
                  to={campaignHref(row.id, "performance")}
                  data-testid={`detail-open-performance-${row.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F2811D] hover:underline"
                >
                  Open the campaign <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
              {posts.length === 0 ? (
                <p className="mt-2 text-sm body-text">Nothing matches on this campaign.</p>
              ) : (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full min-w-[720px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-[#EFEAE1] text-[11px] font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                        <th className="pb-2 pr-3">KOL</th>
                        <th className="pb-2 pr-3">Platform</th>
                        <th className="pb-2 pr-3">Posted</th>
                        <th className="pb-2 pr-3">Reach / Views</th>
                        <th className="pb-2 pr-3">Engagement</th>
                        <th className="pb-2 pr-3">Review</th>
                        <th className="pb-2">Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posts.map((post) => {
                        const insight = post.latest_insight;
                        return (
                          <tr
                            key={post.id}
                            className="border-b border-[#F5F1EA] align-top"
                            data-testid={`detail-post-${post.id}`}
                          >
                            <td className="py-2.5 pr-3">
                              <p className="font-semibold">{post.creator_name}</p>
                              <a
                                href={post.url}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="text-xs font-semibold text-[#F2811D] hover:underline"
                              >
                                Open post
                              </a>
                              {post.is_adjusted && (
                                <p
                                  className="mt-0.5 text-[11px] font-semibold text-[#2C4D8F]"
                                  data-testid={`detail-adjusted-${post.id}`}
                                >
                                  Adjusted by AnyTribe
                                </p>
                              )}
                            </td>
                            <td className="py-2.5 pr-3">
                              <PlatformBadge platform={post.platform} />
                            </td>
                            <td className="py-2.5 pr-3 whitespace-nowrap body-text">
                              {formatDate(post.posting_date)}
                            </td>
                            <td className="py-2.5 pr-3 tabular">
                              {insight
                                ? `${formatNumber(insight.reach)} / ${formatNumber(insight.views)}`
                                : "No insights yet"}
                            </td>
                            <td className="py-2.5 pr-3 tabular">
                              {insight
                                ? formatNumber(
                                    (insight.likes || 0) +
                                      (insight.comments || 0) +
                                      (insight.shares || 0) +
                                      (insight.saves || 0)
                                  )
                                : "—"}
                            </td>
                            <td className="py-2.5 pr-3">
                              <ReviewBadge status={post.review_status} />
                              {insight?.needs_correction && (
                                <p className="mt-0.5 text-[11px] font-semibold text-[#B3261E]">
                                  Needs correction
                                </p>
                              )}
                            </td>
                            <td className="py-2.5 max-w-[200px] text-xs body-text">
                              {post.internal_remark || "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {showInvoices && (
            <section data-testid={`detail-invoices-${row.id}`}>
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">
                  Invoices ({(invoices || []).length})
                </h4>
                <Link
                  to={campaignHref(row.id, "invoices")}
                  data-testid={`detail-open-invoices-${row.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F2811D] hover:underline"
                >
                  Open the campaign <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
              {(invoices || []).length === 0 ? (
                <p className="mt-2 text-sm body-text">No invoices match on this campaign.</p>
              ) : (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full min-w-[640px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-[#EFEAE1] text-[11px] font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                        <th className="pb-2 pr-3">KOL</th>
                        <th className="pb-2 pr-3">Invoice</th>
                        <th className="pb-2 pr-3">Amount</th>
                        <th className="pb-2 pr-3">Status</th>
                        <th className="pb-2 pr-3">Attachment</th>
                        <th className="pb-2">Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => (
                        <tr
                          key={invoice.id}
                          className="border-b border-[#F5F1EA] align-top"
                          data-testid={`detail-invoice-${invoice.id}`}
                        >
                          <td className="py-2.5 pr-3 font-semibold">{invoice.creator_name}</td>
                          <td className="py-2.5 pr-3">
                            {invoice.invoice_number}
                            <span className="block text-xs body-text">
                              {formatDate(invoice.invoice_date)}
                            </span>
                          </td>
                          <td className="py-2.5 pr-3 tabular">{formatRM(invoice.amount_sen)}</td>
                          <td className="py-2.5 pr-3">
                            <Badge
                              tone={
                                invoice.status === "Paid"
                                  ? "green"
                                  : invoice.status === "Changes requested"
                                    ? "red"
                                    : "orange"
                              }
                            >
                              {invoice.status}
                            </Badge>
                          </td>
                          <td className="py-2.5 pr-3">
                            <Attachment file={invoice.attachment} />
                          </td>
                          <td className="py-2.5 max-w-[200px] text-xs body-text">
                            {invoice.internal_remark || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {posts.length === 0 && (invoices || []).length === 0 && (
            <Empty title="Nothing on this campaign yet" hint="Submissions and invoices appear here as they come in." />
          )}
        </div>
      )}
    </div>
  );
}
